{/* eslint-disable-next-line react/no-unescaped-entities */ }
import React, { useState, useEffect, useMemo } from "react";
import {
  Grid,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  MenuItem,
  Select,
} from "@mui/material";
import Fade from "@mui/material/Fade";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import Axios from "axios";
import Cookies from "js-cookie";
import appData from "layouts/orders/data/appData";
import ProductDetailsDialog from "./productDetailsDialog";
import { useMediaQuery } from "@mui/material";


function OrdersDashboard() {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("all");
  const isMobile = useMediaQuery("(max-width:600px)");

  const {
    columns,
    rows,
    notification,
    setNotification,
    isModalOpen,
    fetchProducts,
    handleCloseModal,
    handleCloseNotification,
    handleCloseEditModal,
    isEditModalOpen,
    editedOrder,
    handleSaveChanges,
    setEditedOrder,
  } = appData();

  useEffect(() => {
    const fetchAppointmentsStats = async () => {
      try {
        const { data } = await Axios.get(
          "https://pixelparts-dev-api.up.railway.app/api/v1/stats/orderStats",
          { headers: { Authorization: `Bearer ${Cookies.get("authToken")}` } }
        );
        setStats(data.data.stats);
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAppointmentsStats();
  }, []);

  // Extract the correct payment status from the React component
  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const status = row.paymentStatus?.props?.badgeContent || ""; // Ensure it's a string
      const paymentMethod = row.paymentMethod || "";
      return (
        (filter === "all" || status.toLowerCase().includes(filter.toLowerCase())) &&
        (paymentMethodFilter === "all" ||
          paymentMethod.toLowerCase().includes(paymentMethodFilter.toLowerCase()))
      );
    });
  }, [rows, filter, paymentMethodFilter]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3} marginBottom={5}>
          {[
            {
              title: "Total Orders",
              count: stats.totalorders,
              color: "info",
              icon: "shopping_cart",
            },
            { title: "Paid Orders", count: stats.paidorders, color: "success", icon: "done" },
            {
              title: "Pending Orders",
              count: stats.pendingorders,
              color: "warning",
              icon: "hourglass_empty",
            },
            {
              title: "Cancelled Orders",
              count: stats.cancelledorders,
              color: "error",
              icon: "cancel",
            },
          ].map(({ title, count, color, icon }) => (
            <Grid item xs={12} md={6} lg={3} key={title}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color={color}
                  icon={icon}
                  title={title}
                  count={loading ? <CircularProgress size={20} /> : count}
                  percentage={{ color: "success", amount: "", label: "Just updated" }}
                />
              </MDBox>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="dark"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                justifyContent="space-between"
                alignItems={isMobile ? "flex-start" : "center"}
              >
                <MDTypography variant="h6" color="white">
                  Orders Table
                </MDTypography>
                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    gap: "10px",
                    width: isMobile ? "100%" : "auto",
                  }}
                >
                  {/* Order Status Filter */}
                  <Select
                    value={filter || "all"}
                    onChange={(e) => setFilter(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{
                      padding: "11px",
                      backgroundColor: "rgba(255, 255, 255, 0.5)",
                      borderRadius: "6px",
                      color: "white",
                      minWidth: isMobile ? "100%" : "150px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255, 255, 255, 0.3)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#ffffff" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1e88e5" },
                    }}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="Paid">Paid</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Cancelled">Cancelled</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                  </Select>

                  {/* Payment Method Filter */}
                  <Select
                    value={paymentMethodFilter || "all"}
                    onChange={(e) => setPaymentMethodFilter(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{
                      padding: "11px",
                      backgroundColor: "rgba(255, 255, 255, 0.5)",
                      borderRadius: "6px",
                      color: "white",
                      minWidth: isMobile ? "100%" : "150px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255, 255, 255, 0.3)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#ffffff" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1e88e5" },
                    }}
                  >
                    <MenuItem value="all">All Methods</MenuItem>
                    <MenuItem value="card">Credit Card</MenuItem>
                    <MenuItem value="bank">Bank Transfer</MenuItem>
                  </Select>
                </div>
              </MDBox>


              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows: filteredRows }}
                  isSorted
                  entriesPerPage={false}
                  showTotalEntries
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />

      {/* Edit Order Dialog */}
      <Dialog
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 500 }}
      >
        <DialogTitle>Update Order Status</DialogTitle>
        <DialogContent>
          {editedOrder && (
            <Select
              name="orderState"
              value={editedOrder.orderStatus ? editedOrder.orderStatus : ""}
              onChange={(e) => setEditedOrder((prev) => ({ ...prev, orderStatus: e.target.value }))}
              fullWidth
              margin="dense"
              sx={{
                marginTop: "1px",
                padding: "11px",
                backgroundColor: "rgba(0, 0, 0, 0.1)",
                borderRadius: "6px",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0, 0, 0, 0.3)" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#1e88e5" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1e88e5" },
              }}
            >
              {/*       const validStatuses = ["Pending", "Paid", "Cancelled", "Completed"]; */}
              <MenuItem value="Paid">Paid</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Cancelled">Cancelled</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
            </Select>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSaveChanges} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <MDSnackbar
        color={notification.severity}
        icon={notification.severity === "success" ? "check" : "error"}
        title={notification.severity === "success" ? "Success" : "Error"}
        content={notification.message}
        open={notification.open}
        onClose={handleCloseNotification}
        close={handleCloseNotification}
        bgWhite
      />

      {/* Product Details Dialog */}
      <ProductDetailsDialog
        isOpen={isModalOpen}
        handleClose={handleCloseModal}
        fetchProducts={fetchProducts}
      />
    </DashboardLayout>
  );
}

export default OrdersDashboard;
